import React from "react";
import { Logo } from "./shared";
import * as m from "../lib/model";
import { qsbsBump } from "./room/Qsbs";

const fmtShort = (n) => {
  const a = Math.abs(n || 0);
  if (a >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return m.fmt$(n);
};

// Investor-style blind teaser, self-contained from the live deal model.
// Used on the Teaser Generator page and rendered hidden inside the email composer for "Attach Teaser".
export const DealTeaser = React.forwardRef(({ M, displayName, industry, location, overview, secs, page1Ref, page2Ref, testid = "teaser-doc" }, ref) => {
  const name = displayName || M.dealName || M.companyName || "Confidential Opportunity";
  const ind = industry || M.industry || "operating business";
  const loc = location || M.address || "United States";
  const show = secs || { "Company overview": true, "Financial snapshot": true, "Revenue trends": true, "Senior debt terms": true, "Equity structure": true, "Exit summary": true };

  const ex = m.exitReturns(M);
  const wf = m.lpWaterfall(M);
  const bump = qsbsBump(M);
  const holdLabel = M.holdIndefinite ? "Indefinite (no exit)" : `${M.hold} years`;
  const dscr = m.dscrYr1(M);
  const hist = (M.historicalYears || []).slice(-3);
  const g = 1 + (M.sdeGrowth || 0) / 100;
  const last = hist[hist.length - 1] || { revenue: M.ttmRevenue, gm: M.grossMarginPct, netIncome: 0, sde: M.sde };
  const forecast = { year: "FY+1 (F)", revenue: Math.round((last.revenue || 0) * g), gm: last.gm || M.grossMarginPct, netIncome: Math.round((last.netIncome || 0) * g), sde: Math.round((M.sde || 0) * g), isF: true };
  const finCols = [...hist, forecast];
  const barMax = Math.max(...finCols.map((c) => c.revenue || 0), 1);

  return (
    <div className="teaser" data-testid={testid} ref={ref}>
      <div className="t-page" ref={page1Ref} data-testid="teaser-page-1">
      <div className="t-hero">
        <div>
          <div className="t-eyebrow">Investment Opportunity</div>
          <h1>{name}</h1>
          <div className="t-sub">{ind} · {loc}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <Logo height={56} />
          <div className="t-contact">{M.analystName || "XYZ Ventures"}<br />Confidential — Available under NDA</div>
        </div>
      </div>

      {show["Company overview"] && (
        <div className="t-overview">
          <h2>Company Overview</h2>
          <p>{overview || `An established ${String(ind).toLowerCase()} operator in the ${loc} with a durable, largely recurring revenue base and a clean owner-transition opportunity. The business benefits from long-tenured customer relationships, a seasoned crew and consistent margins, positioning it well for a smooth transition and continued growth under new ownership.`}</p>
        </div>
      )}

      <div className="t-grid t-grid-a">
        <div>
          <div className="t-sec">
            <h2>General Info</h2>
            <ul className="t-info">
              <li><span className="k">Industry</span><span className="v">{ind}</span></li>
              <li><span className="k">Headquarters</span><span className="v">{loc}</span></li>
              {M.yearEstablished ? <li><span className="k">Founded</span><span className="v">{M.yearEstablished}</span></li> : null}
              {M.employeeCount ? <li><span className="k">Employees</span><span className="v">{M.employeeCount}</span></li> : null}
              <li><span className="k">Deal type</span><span className="v">{M.dealType}</span></li>
              {M.reasonForSale ? <li><span className="k">Reason for sale</span><span className="v">{M.reasonForSale}</span></li> : null}
              <li><span className="k">Asking price</span><span className="v">{m.fmt$(M.askPrice)}</span></li>
            </ul>
          </div>
        </div>
        <div>
          {show["Financial snapshot"] && (
            <div className="t-sec">
              <h2>Financial Snapshot</h2>
              <div className="t-fin-wrap">
                <table className="t-fin">
                  <thead><tr><th>$</th>{finCols.map((c) => <th key={c.year} className={c.isF ? "fc" : ""}>{c.year}</th>)}</tr></thead>
                  <tbody>
                    <tr><td>Revenue</td>{finCols.map((c) => <td key={c.year} className={c.isF ? "fc" : ""}>{m.fmt$(c.revenue)}</td>)}</tr>
                    <tr><td>Margin</td>{finCols.map((c) => <td key={c.year} className={c.isF ? "fc" : ""}>{m.fmtPct(m.pctOf(c.gm))}</td>)}</tr>
                    <tr><td>Net Income</td>{finCols.map((c) => <td key={c.year} className={c.isF ? "fc" : ""}>{m.fmt$(c.netIncome)}</td>)}</tr>
                    <tr className="hl"><td>SDE/EBITDA</td>{finCols.map((c) => <td key={c.year} className={c.isF ? "fc" : ""}>{m.fmt$(c.sde)}</td>)}</tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div>
          {show["Revenue trends"] && (
            <div className="t-sec">
              <h2>Revenue Trends</h2>
              <div className="t-bars">
                {finCols.map((c) => (
                  <div className="t-bar" key={c.year}>
                    <div className="amt">{fmtShort(c.revenue)}</div>
                    <div className={`bar ${c.isF ? "fc" : ""}`} style={{ height: `${Math.max(6, ((c.revenue || 0) / barMax) * 100)}%` }} />
                    <div className="cap">{c.year}</div>
                  </div>
                ))}
              </div>
              <div className="t-callouts">
                <div className="t-callout"><div className="lbl">TTM Gross Revenue</div><div className="num">{fmtShort(last.revenue)}</div></div>
                <div className="t-callout"><div className="lbl">Forecast Growth</div><div className="num">{M.sdeGrowth >= 0 ? "+" : ""}{M.sdeGrowth}%</div></div>
              </div>
            </div>
          )}
        </div>
      </div>

      </div>

      <div className="t-page" ref={page2Ref} data-testid="teaser-page-2">
      <div className="t-grid t-grid-b">
        <div>
          {show["Senior debt terms"] && (
            <div className="t-sec" style={{ marginBottom: 0 }}>
              <h2>Senior Debt Terms</h2>
              <ul className="t-terms">
                <li><span className="k">Base loan amount</span><span className="v">{m.fmt$(m.loanAmt(M))}</span></li>
                <li><span className="k">Interest rate</span><span className="v">{M.rate}%</span></li>
                <li><span className="k">Term</span><span className="v">{M.loanYrs} years</span></li>
                <li><span className="k">DSCR (Year 1)</span><span className="v">{m.fmtX(dscr)}</span></li>
              </ul>
            </div>
          )}
        </div>
        <div>
          {show["Equity structure"] && (
            <div className="t-sec" style={{ marginBottom: 0 }}>
              <h2>Equity Structure &amp; Terms</h2>
              <ul className="t-terms">
                <li><span className="k">Sponsor / investor equity</span><span className="v">{m.fmt$(ex.equityInvested)}</span></li>
                <li><span className="k">Preferred return</span><span className="v">{M.prefReturn}% cumulative</span></li>
                {M.snAmt > 0 ? <li><span className="k">Seller note</span><span className="v">{m.fmt$(M.snAmt)} @ {M.snRate}%</span></li> : null}
                {M.rollPct > 0 ? <li><span className="k">Seller rollover</span><span className="v">{M.rollPct}%</span></li> : null}
                <li><span className="k">Hold period</span><span className="v">{holdLabel}</span></li>
                <li><span className="k">Modeled LP MOIC</span><span className="v">{m.fmtX(wf.lpMoic)}{bump ? ` · QSBS +${m.fmtX(bump.x)} after tax` : ""}</span></li>
                <li><span className="k">Modeled LP IRR</span><span className="v">{isNaN(wf.lpIrr) ? "n/a" : m.fmtPct(wf.lpIrr)}{bump && bump.bps > 0 ? ` · QSBS +${bump.bps} bps after tax` : ""}</span></li>
              </ul>
            </div>
          )}
        </div>
        <div>
          {show["Exit summary"] && (
            <div className="t-sec" style={{ marginBottom: 0 }}>
              <h2>Exit Summary</h2>
              <ul className="t-terms">
                <li><span className="k">Exit SDE</span><span className="v">{m.fmt$(ex.sdeExit)}</span></li>
                <li><span className="k">Exit enterprise value</span><span className="v">{M.holdIndefinite ? "No exit — cash-flow hold" : `${m.fmt$(ex.exitEV)} (${M.exitMult}×)`}</span></li>
                <li><span className="k">Remaining loan balance</span><span className="v">{m.fmt$(ex.loanBal)}</span></li>
                <li><span className="k">Equity at exit</span><span className="v">{M.holdIndefinite ? "No exit — cash-flow hold" : m.fmt$(ex.equityAtExit)}</span></li>
                {ex.rollOwnPct > 0.01 ? <li><span className="k">Seller rollover (common only)</span><span className="v">{m.fmtPct(ex.rollOwnPct)} · {m.fmt$(ex.rollExit)} of exit residual</span></li> : null}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="t-foot">XYZ Ventures · {ind} · {loc} · Confidential — available under NDA</div>
      </div>
    </div>
  );
});
